import Axios, {AxiosInstance} from 'axios';
import {ITicketDTO} from '@/TPWebClient/dtos/ticket.dto';
import {ReplaySubject, Subject} from 'rxjs';
import {IBadgeDTO} from '@/TPWebClient/dtos/badge.dto';
import {ILocationDTO} from '@/TPWebClient/dtos/location.dto';
import moment from 'moment-timezone';
import {findIndex, differenceWith, isEqual} from 'lodash';
import {IApiResponseDTO} from '@/TPWebClient/dtos/api-response.dto';
import {ICustomerDTO} from '@/TPWebClient/dtos/customer.dto';


export interface IGenericResponse<T> {
	data: IApiResponseDTO<T>;
}

export interface IUpdateTicket {
	badgeId: string | null;
	total: number;
	customer: ICustomerDTO;
	partySize: number;
	waitTime: number | null;
	orderNotes: string | null;
}

export interface ICreateTicket {
	badgeId: string | null;
	total: number;
	customer: ICustomerDTO;
	partySize: number;
	waitTime: number | null;
	orderNotes: string;
}

export interface IUpdateBadge {
	name: string;
}

export default class TPWebClient<LocationDTO extends ILocationDTO = ILocationDTO, TicketDTO extends ITicketDTO = ITicketDTO> {
	protected readonly axios: AxiosInstance;
	protected readonly host!: string;
	// protected readonly host = 'https://c20.tp-devel.com/api/v1/location/';
	protected readonly locationId!: string;
	protected readonly apiKey!: string;
	protected readonly updateMethod: 'poll' | 'ws' | 'none' = 'poll';
	protected readonly updateOptions: {interval?: number} = {};
	protected running = true;

	protected readonly modulesToLoad!: Array<'tickets' | 'badges' | 'location'>;
	protected loadedModules: Array<'tickets' | 'badges' | 'location'> = [];

	protected ready: Promise<any> = Promise.resolve();
	protected websocket: WebSocket | null = null;
	protected ignoreDirtyClose = false;
	protected pollInterval = 10;

	// Tickets
	protected ticketsData: TicketDTO[] = [];
	protected ticketsInitialLoadComplete = false;
	protected ticketsWSBuffer: TicketDTO[] = [];
	protected ticketsReplaySubject: ReplaySubject<TicketDTO[]> = new ReplaySubject<TicketDTO[]>(1);
	protected ticketSubject: Subject<TicketDTO> = new Subject<TicketDTO>();
	protected ticketClosedSubject: Subject<string> = new Subject<string>();
	protected ticketsReady: Promise<void> = Promise.resolve();
	protected ticketTimeout: number | null = null;

	// Location
	protected locationData: LocationDTO | null = null;
	protected locationReplaySubject: ReplaySubject<LocationDTO> = new ReplaySubject<LocationDTO>(1);
	protected locationSubject: Subject<LocationDTO> = new Subject<LocationDTO>();
	protected locationsReady: Promise<void> = Promise.resolve();

	// Badges
	protected badgesData: IBadgeDTO[] = [];
	protected badgesInitialLoadComplete = false;
	protected badgesWSBuffer: IBadgeDTO[] = [];
	protected badgesDeleteWSBuffer: string[] = [];
	protected badgesReplaySubject: ReplaySubject<IBadgeDTO[]> = new ReplaySubject<IBadgeDTO[]>(1);
	protected badgeSubject: Subject<IBadgeDTO> = new Subject<IBadgeDTO>();
	protected badgeDeletedSubject: Subject<string> = new Subject<string>();
	protected badgesReady: Promise<void> = Promise.resolve();
	protected badgeTimeout: number | null = null;

	constructor(
		host: string,
		apiKey: string,
		locationId: string,
		modulesToLoad: Array<'tickets' | 'badges'> = ['tickets', 'badges'],
		updateMethod: 'poll' | 'ws' | 'none' = 'poll',
		updateOptions: {interval?: number} = {}
	) {
		this.host = host;
		this.apiKey = apiKey;
		this.locationId = locationId;
		this.modulesToLoad = modulesToLoad;
		this.updateMethod = updateMethod;
		this.updateOptions = updateOptions;
		// Set defaults if not given
		if(this.updateMethod === 'poll') {
			this.updateOptions = Object.assign({}, {interval: 10}, this.updateOptions);
		}

		this.axios = Axios.create({
			baseURL: this.host + this.locationId,
			timeout: 30000,
			headers: {
				'Api-Key': this.apiKey
			}
		});
	}

	public async testAuth() {
		// Just try to fetch the location and see if it blows up
		try {
			await this.axios({
				method: 'GET',
				url: '',
			});
		} catch(e) {
			console.log(e);
			throw new Error('Invalid Credentials');
		}
	}

	public async start() {
		this.running = true;
		const promises: Promise<any>[] = [];
		this.loadedModules = [];
		this.ticketsInitialLoadComplete = false;
		this.ticketsWSBuffer = [];

		// Location needs to always be fetched
		this.locationsReady = this.fetchLocation();
		await this.locationsReady;
		if(this.updateMethod === 'ws') {
			await this.setupWS();
		} else if(this.updateMethod === 'poll') {
			this.pollInterval = this.updateOptions.interval as number;
		}

		for(const moduleToLoad of this.modulesToLoad) {
			switch(moduleToLoad) {
				case 'badges':
					if(this.updateMethod === 'poll') {
						// Delay first start
						this.badgeTimeout = setTimeout(() => {
							this.pollBadges();
						}, this.pollInterval * 1000);
					}
					this.badgesReady = this.fetchBadges();
					promises.push(this.badgesReady);
					break;
				case 'tickets':
					// Setup poll here if selected, WS is a global thing
					if(this.updateMethod === 'poll') {
						// Delay first start
						this.ticketTimeout = setTimeout(() => {
							this.pollTickets();
						}, this.pollInterval * 1000);
					}
					this.ticketsReady = this.fetchTickets();
					promises.push(this.ticketsReady);
					break;
			}
			this.loadedModules.push(moduleToLoad);
		}
		return this.ready = Promise.all(promises);
	}

	public stop() {
		this.running = false;
		if(this.updateMethod === 'poll') {
			if(this.ticketTimeout !== null) {
				clearTimeout(this.ticketTimeout);
				this.ticketTimeout = null;
			}
			if(this.badgeTimeout !== null) {
				clearTimeout(this.badgeTimeout);
				this.badgeTimeout = null;
			}
		} else if(this.updateMethod === 'ws') {
			if(this.websocket !== null) {
				// This `ignoreDirtyClose` is just needed until TP implements the
				// correct close logic for WS
				this.ignoreDirtyClose = true;
				this.websocket.close();
				setTimeout(() => {
					this.websocket = null;
					this.ignoreDirtyClose = false;
				}, 500);

			}
		}
	}

	private async fetchBadges() {
		const result = await this.axios({
			method: 'GET',
			url: 'badges',
		}) as IGenericResponse<{badges: IBadgeDTO[]}>;
		const badges = result.data.data.badges;
		const newOrUpdatedBadges = differenceWith(badges, this.badgesData, isEqual);
		this.badgesData = badges;
		this.badgesReplaySubject.next(this.badgesData);
		newOrUpdatedBadges.forEach((badge) => {
			this.badgeSubject.next(badge);
		});
		this.drainBadgesWS();
		this.drainBadgesDeleteWS();
		this.badgesInitialLoadComplete = true;
	}

	private async fetchLocation() {
		const result = await this.axios({
			method: 'GET',
			url: '',
		}) as IGenericResponse<{location: LocationDTO}>;
		this.locationData = result.data.data.location;
		this.locationReplaySubject.next(this.locationData);
		this.locationSubject.next(this.locationData);
	}

	private async fetchTickets() {
		const result = await this.axios({
			method: 'GET',
			url: 'tickets',
		}) as IGenericResponse<{tickets: TicketDTO[]}>;
		const tickets = result.data.data.tickets;
		const newOrUpdatedTickets = differenceWith(tickets, this.ticketsData, isEqual);
		this.ticketsData = tickets;
		this.ticketsReplaySubject.next(this.ticketsData);
		newOrUpdatedTickets.forEach((ticket) => {
			this.ticketSubject.next(ticket);
		});
		this.drainTicketsWS();
		this.ticketsInitialLoadComplete = true;
	}

	private async setupWS() {
		return new Promise((resolve, reject) => {
			if(this.locationData === null) {
				reject('Missing Location Data');
				return;
			}
			this.websocket = new WebSocket(this.locationData.wssUrl);

			this.websocket.onopen = (e) => {
				console.log('[open] Connection established');
				resolve();
			};

			this.websocket.onmessage = (event) => {
				const start = event.data.indexOf(':');
				const end = event.data.indexOf('{');
				const ack = 'ACK:' + event.data.substring(start + 1, end - 1); // +1 for ":", -1 for trailing space
				this.websocket?.send(ack);
				const payload = JSON.parse(event.data.substring(end));
				const type = payload.notify;
				const data = JSON.parse(payload.object);
				switch(type) {
					case 'notify_push_sdp_ticket_update':
						console.log('[notify_push_sdp_ticket_update]', data);
						if(this.ticketsInitialLoadComplete) {
							this.onTicketUpdate(data.ticket);
						} else {
							this.ticketsWSBuffer.push(data.ticket);
						}
						break;
					case 'notify_push_sdp_badge_update':
						console.log('[notify_push_sdp_badge_update]', data);
						if(this.badgesInitialLoadComplete) {
							this.onBadgeUpdate(data.badge);
						} else {
							this.badgesWSBuffer.push(data.badge);
						}
						break;
					case 'notify_push_sdp_badge_delete':
						console.log('[notify_push_sdp_badge_delete]', data);
						if(this.badgesInitialLoadComplete) {
							this.onBadgeDelete(data.badge_id);
						} else {
							this.badgesDeleteWSBuffer.push(data.badge_id);
						}
						break;
					default:
						console.log('Unknown push type: ' + type, data);
						break;
				}
			};

			this.websocket.onclose = (event) => {
				console.log('CLOSE', event);
				if (event.wasClean) {
					console.log(`[close] Connection closed cleanly, code=${event.code} reason=${event.reason}`);
				} else if(!this.ignoreDirtyClose) {
					// e.g. server process killed or network down
					// event.code is usually 1006 in this case
					console.log('[close] Connection died', event);
					console.log('Restarting API');
					this.start();
				}
			};

			this.websocket.onerror = (error: any) => {
				console.log(`[error] ${error.message}`);
				reject();
			};
		});
	}

	private drainTicketsWS() {
		this.ticketsWSBuffer.forEach((ticket) => {
			this.onTicketUpdate(ticket);
		});
		this.ticketsWSBuffer = [];
	}

	private drainBadgesWS() {
		this.badgesWSBuffer.forEach((badge) => {
			this.onBadgeUpdate(badge);
		});
		this.badgesWSBuffer = [];
	}

	private drainBadgesDeleteWS() {
		this.badgesDeleteWSBuffer.forEach((badgeId) => {
			this.onBadgeDelete(badgeId);
		});
		this.badgesDeleteWSBuffer = [];
	}

	private onTicketUpdate(ticket: TicketDTO) {
		// @ts-ignore - Not sure why this is yelling, we know that ticket/ticketsData[*] will have a ticketId on it
		const foundTicketIndex = findIndex(this.ticketsData, {ticketId: ticket.ticketId});
		if(foundTicketIndex === -1) {
			if(ticket.closedDate === null) {
				// This is a new ticket and it is not closed, add it to internal array
				// and push full array and new item
				this.ticketsData.push(ticket);
				this.ticketsReplaySubject.next(this.ticketsData);
				this.ticketSubject.next(ticket);
			} else {
				// This would be an odd case for sure but we will handle it
				this.ticketClosedSubject.next(ticket.ticketId);
			}
		} else if(this.ticketsData[foundTicketIndex].modId < ticket.modId) {
			if(ticket.closedDate === null) {
				// We have an update to an existing so let's update it internally then
				// push the full array and new item
				this.ticketsData.splice(foundTicketIndex, 1, ticket);
				this.ticketsReplaySubject.next(this.ticketsData);
				this.ticketSubject.next(ticket);
			} else {
				// We have an update and the ticket is closed so let's remove it internally then
				// push the full array and canceled id
				this.ticketsData.splice(foundTicketIndex, 1);
				this.ticketsReplaySubject.next(this.ticketsData);
				this.ticketClosedSubject.next(ticket.ticketId);
			}
		}
	}


	private onBadgeUpdate(badge: IBadgeDTO) {
		const foundTicketIndex = findIndex(this.badgesData, {badgeId: badge.badgeId});
		if(foundTicketIndex === -1) {
			// This is a new badge, add it to internal array
			// and push full array and new item
			this.badgesData.push(badge);
			this.badgesReplaySubject.next(this.badgesData);
			this.badgeSubject.next(badge);
		} else if(this.badgesData[foundTicketIndex].modId < badge.modId) {
			// We have an update to an existing so let's update it internally then
			// push the full array and new item
			this.badgesData.splice(foundTicketIndex, 1, badge);
			this.badgesReplaySubject.next(this.badgesData);
			this.badgeSubject.next(badge);
		}
	}

	private onBadgeDelete(badgeId: string) {
		const foundTicketIndex = findIndex(this.badgesData, {badgeId: badgeId});
		if(foundTicketIndex === -1) {
			// This would be an odd case for sure but we will handle it
			this.badgeDeletedSubject.next(badgeId);
		} else {
			// We have an update and the badge is closed so let's remove it internally then
			// push the full array and canceled id
			this.badgesData.splice(foundTicketIndex, 1);
			this.badgesReplaySubject.next(this.badgesData);
			this.badgeDeletedSubject.next(badgeId);
		}
	}

	private async pollTickets() {
		console.log('[Tickets] Polling...');

		const lastUpdateStart = moment().unix();
		const result = await this.axios({
			method: 'GET',
			url: 'tickets',
		}) as IGenericResponse<{tickets: TicketDTO[]}>;


		const tickets = result.data.data.tickets;
		const newOrUpdatedTickets = differenceWith(tickets, this.ticketsData, isEqual);
		const removedTickets = differenceWith(this.ticketsData, tickets, (a, b) => {
			return a.ticketId === b.ticketId;
		});

		if(newOrUpdatedTickets.length || removedTickets.length) {
			// We don't need to update tickets, this is the new state so we can
			// just overwrite it and push out the full state. Then send out updates for
			// added/updated and closed tickets
			this.ticketsData = tickets;
			this.ticketsReplaySubject.next(this.ticketsData);
			newOrUpdatedTickets.forEach((ticket) => {
				this.ticketSubject.next(ticket);
			});
			removedTickets.forEach((ticket) => {
				this.ticketClosedSubject.next(ticket.ticketId);
			});
		}

		// Fire off next poll either X seconds (pollInterval) after last fire or now if it's
		// been longer than pollInterval since we last fired
		const timeTillNextUpdate = (this.pollInterval) - (moment().unix() - lastUpdateStart);

		// We will cancel the timeout but if we are in the middle of a fetch we want to bail here
		if(this.running) {
			this.ticketTimeout = setTimeout(() => {
				this.pollTickets();
			}, timeTillNextUpdate > 0 ? timeTillNextUpdate * 1000 : 0);
		}
	}

	private async pollBadges() {
		console.log('[Badges] Polling...');

		const lastUpdateStart = moment().unix();
		const result = await this.axios({
			method: 'GET',
			url: 'badges',
		}) as IGenericResponse<{badges: IBadgeDTO[]}>;


		const badges = result.data.data.badges;
		const newOrUpdatedBadges = differenceWith(badges, this.badgesData, isEqual);
		const removedBadges = differenceWith(this.badgesData, badges, (a, b) => {
			return a.badgeId === b.badgeId;
		});

		if(newOrUpdatedBadges.length || removedBadges.length) {
			// We don't need to update badges, this is the new state so we can
			// just overwrite it and push out the full state. Then send out updates for
			// added/updated and closed badges
			this.badgesData = badges;
			this.badgesReplaySubject.next(this.badgesData);
			newOrUpdatedBadges.forEach((badge) => {
				this.badgeSubject.next(badge);
			});
			removedBadges.forEach((badge) => {
				this.badgeDeletedSubject.next(badge.badgeId);
			});
		}

		// Fire off next poll either X seconds (pollInterval) after last fire or now if it's
		// been longer than pollInterval since we last fired
		const timeTillNextUpdate = (this.pollInterval) - (moment().unix() - lastUpdateStart);

		// We will cancel the timeout but if we are in the middle of a fetch we want to bail here
		if(this.running) {
			this.badgeTimeout = setTimeout(() => {
				this.pollBadges();
			}, timeTillNextUpdate > 0 ? timeTillNextUpdate * 1000 : 0);
		}
	}

	badgesUpdated() {
		if(!this.modulesToLoad.includes('badges')) {
			throw new Error('Badge module not loaded');
		} else {
			return this.badgesReplaySubject;
		}
	}

	badgeUpdated() {
		if(!this.modulesToLoad.includes('badges')) {
			throw new Error('Badge module not loaded');
		} else {
			return this.badgeSubject;
		}
	}

	badgeDeleted() {
		if(!this.modulesToLoad.includes('badges')) {
			throw new Error('Badge module not loaded');
		} else {
			return this.badgeDeletedSubject;
		}
	}

	ticketsUpdated() {
		if(!this.modulesToLoad.includes('tickets')) {
			throw new Error('Ticket module not loaded');
		} else {
			return this.ticketsReplaySubject;
		}
	}

	ticketUpdated() {
		if(!this.modulesToLoad.includes('tickets')) {
			throw new Error('Ticket module not loaded');
		} else {
			return this.ticketSubject;
		}
	}

	ticketClosed() {
		if(!this.modulesToLoad.includes('tickets')) {
			throw new Error('Ticket module not loaded');
		} else {
			return this.ticketClosedSubject;
		}
	}

	locationUpdated() {
		return this.locationReplaySubject;
	}

	async createTicket(createTicket: ICreateTicket): Promise<IGenericResponse<{ticket: TicketDTO}>> {
		return this.axios({
			method: 'POST',
			url: 'tickets',
			data: createTicket
		});
	}

	async updateTicket(ticketId: string, updateTicket: IUpdateTicket): Promise<IGenericResponse<{ticket: TicketDTO}>> {
		return this.axios({
			method: 'POST',
			url: 'tickets/' + ticketId + '/update',
			data: updateTicket
		});
	}

	async closeTicket(ticketId: string): Promise<IGenericResponse<{ticket: TicketDTO}>> {
		return this.axios({
			method: 'GET',
			url: 'tickets/' + ticketId + '/close',
		});
	}

	async deletePayment(ticketId: string, paymentId: string) {
		return this.axios({
			method: 'POST',
			url: 'tickets/' + ticketId + '/payments/' + paymentId + '/delete',
		});
	}

	async sendSms(ticketId: string, phoneNumber: string) {
		return this.axios({
			method: 'POST',
			url: 'tickets/' + ticketId + '/sms',
			data: {
				phone: phoneNumber
			}
		});
	}

	async searchCustomers(phoneNumber: string) {
		return this.axios({
			method: 'POST',
			url: 'customers/search',
			data: {
				phone: phoneNumber
			}
		});
	}

	async createBadge(name: string): Promise<IGenericResponse<{badge: IBadgeDTO}>> {
		return this.axios({
			method: 'POST',
			url: 'badges',
			data: {
				name: name
			}
		});
	}

	async updateBadge(badgeId: string, updateBadge: IUpdateBadge): Promise<IGenericResponse<{badge: IBadgeDTO}>> {
		return this.axios({
			method: 'POST',
			url: 'badges/' + badgeId + '/edit',
			data: updateBadge
		});
	}

	async deleteBadge(badgeId: string) {
		return this.axios({
			method: 'GET',
			url: 'badges/' + badgeId + '/delete',
		});
	}

	async sendSmsAccess(phoneNumber: string) {
		return this.axios({
			method: 'POST',
			url: 'auth/sms_access',
			data: {
				phone: phoneNumber
			}
		});
	}
}
